import { ErrorHandler, Injectable } from '@angular/core';
import { trace } from '@opentelemetry/api';
import { decrireErreur } from '@services/core/telemetrie';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private readonly chunkReloadStorageKey = 'atlas-chunk-reload-attempted';

  private isChunkLoadError(error: any): boolean {
    const text = [
      error?.name,
      error?.message,
      error?.stack,
      error?.rejection?.name,
      error?.rejection?.message,
      error?.reason?.message,
      String(error?.rejection || ''),
      String(error || ''),
    ].join(' ');

    return /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(text);
  }

  private reloadOnceAfterChunkError(error: any): boolean {
    if (!this.isChunkLoadError(error)) return false;

    try {
      if (sessionStorage.getItem(this.chunkReloadStorageKey) === '1') return false;
      sessionStorage.setItem(this.chunkReloadStorageKey, '1');
    } catch {}

    window.location.reload();
    return true;
  }

  private getUserContext(): { username?: string; email?: string } {
    // Extract user from localStorage (MSAL patterns)
    try {
      // Try stable sentryUser first
      const stable = localStorage.getItem('sentryUser');
      if (stable) {
        try {
          const u = JSON.parse(stable);
          return {
            username: u?.username || u?.name,
            email: u?.email,
          };
        } catch {
          return { username: stable };
        }
      }

      // Fallback: scan common MSAL keys
      const keys = ['msal.active-account', 'username', 'userName', 'user', 'current_user', 'msal.account'];
      for (const k of keys) {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        try {
          const obj = JSON.parse(raw);
          const username = obj?.username || obj?.userName || obj?.name || obj?.displayName || obj?.preferred_username;
          const email = obj?.email || obj?.preferred_username;
          if (username || email) {
            return { username, email };
          }
        } catch {
          // Not JSON, maybe plain string
          if (k === 'username' || k === 'userName') {
            return { username: raw };
          }
        }
      }
    } catch (err) {
      // Silently fail if localStorage access fails
    }
    return {};
  }

  handleError(error: Error | any): void {
    if (this.reloadOnceAfterChunkError(error)) return;

    // Get the tracer from the global provider
    const tracer = trace.getTracer('atlas-v2-web');
    
    // Get user context
    const userContext = this.getUserContext();
    
    // Create attributes with error info and user context
    const attributes: Record<string, any> = {
      'error.type': error?.name || 'UnknownError',
      'error.message': error?.message || String(error),
      'error.stack': error?.stack || '',
    };

    // Une HttpErrorResponse porte l'essentiel HORS de son message : URL, statut, corps
    // renvoyé par le serveur. Sans eux, 6 652 erreurs « Http failure response for
    // (unknown url): undefined undefined » se sont accumulées en trois jours chez 93
    // utilisateurs sans qu'on puisse dire d'où elles venaient. decrireErreur les
    // extrait (http.url, http.status_code, message enrichi) ; on les ajoute sans
    // écraser ce qui précède.
    if (error && typeof error === 'object' && !(error instanceof Error)) {
      const detail = decrireErreur(error);
      for (const [cle, valeur] of Object.entries(detail.attributs)) {
        attributes[cle] = valeur;
      }
      if (detail.message && detail.message !== attributes['error.message']) {
        attributes['error.detail'] = detail.message;
      }
      const corps = (error as any).error;
      if (corps !== undefined && corps !== null) {
        try {
          attributes['http.body'] = (typeof corps === 'string' ? corps : JSON.stringify(corps)).slice(0, 1000);
        } catch { /* corps non sérialisable */ }
      }
    }
    
    // Add user context if available
    if (userContext.username) {
      attributes['user.name'] = userContext.username;
    }
    if (userContext.email) {
      attributes['user.email'] = userContext.email;
    }
    
    // Create a span for this error
    const span = tracer.startSpan('angular.error', { attributes });
    
    // Record the exception with full context
    if (error instanceof Error) {
      span.recordException(error);
    } else {
      span.recordException(new Error(String(error)));
    }
    
    span.end();
    
    // Still log to console for debugging
    console.error('Angular error caught:', error);
  }
}
